import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ImageSlider } from "../../../components";
import { useStaticQuery, graphql } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

const Dosing = () => {
  const { dosingScheme, dosingChart } = useStaticQuery(
    graphql`
      query {
        dosingScheme: file(
          relativePath: { eq: "assets/images/gastro-dosing-scheme.png" }
        ) {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, quality: 100)
          }
        }
        dosingChart: file(
          relativePath: { eq: "assets/images/dosing-strength-chart.png" }
        ) {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, quality: 100)
          }
        }
      }
    `,
  );
  return (
    <>
      <Container
        header
        initial={{ y: 100, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <SectionHeader>
          <TitlePosition>
            <Title>Dosing</Title>
          </TitlePosition>
          <HeaderContent>
            <h4>
              UZPRUVO® IN GASTROENTEROLOGY:
              <br />
              <span>
                POSOLOGY AND ADMINISTRATION OPTIONS
                <sup>
                  <small>1</small>
                </sup>
              </span>
            </h4>
            <Text>
              In addition to intravenous administration, choose between two
              subcutaneous options and dose strengths to suit individual
              maintenance treatment needs
            </Text>
            <ImageContainer>
              <GatsbyImage image={getImage(dosingScheme)} />
            </ImageContainer>
            <SmallText>
              V, intravenous; PFS, pre-filled syringe; SC, subcutaneous
              <br />
              1. Uzpruvo® SmPC
            </SmallText>
          </HeaderContent>
        </SectionHeader>
        <InnerContainer>
          <h4>
            <span>
              Dosing Scheme
              <sup>
                <small>1</small>
              </sup>
            </span>
          </h4>
          <ImageSlider image={dosingChart} />
          <p>
            <strong>PFS</strong>, pre-filled syringe; <strong>Q8W</strong>,
            every 8 weeks; <strong>Q12W</strong>, every 12 weeks;{" "}
            <strong>SC</strong>, subcutaneous *The approved ustekinumab
            treatment regimen for patients with Crohn's disease is initiation
            with a single intravenous dose based on body weight. Uzpruvo
            <sup>
              <small>®</small>
            </sup>{" "}
            is available in pre-filled syringes for subcutaneous use. After the
            first intravenous infusion induction dose with an alternative
            ustekinumab product, patients can receive subcutaneous maintenance
            doses with Uzpruvo
            <sup>
              <small>®</small>
            </sup>
            .
            <sup>
              <small>1</small>
            </sup>
          </p>
        </InnerContainer>
      </Container>
    </>
  );
};

const Container = styled(motion.section)`
  color: #ffffff;
  display: grid;
  position: relative;
  grid-template-columns: repeat(6, 1fr);
  h4 {
    font-size: 1.125rem;
    font-weight: 300;
    text-transform: uppercase;
    span {
      font-weight: 500;
    }
  }

  h5 {
    font-size: 1.25rem;
    font-weight: 500;
    text-transform: uppercase;
    margin-top: 1rem;
  }

  p {
    font-weight: 300;
  }
  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(12, 1fr);
    h4 {
      font-size: 1.625rem;
    }

    h5 {
      font-size: 1.5rem;
    }
  }
`;

const Title = styled.div`
  align-items: center;
  background: var(--uzpruvo-orange);
  border-bottom-right-radius: 40px 40px;
  border-top-right-radius: 40px 40px;
  border-bottom: 1px solid #ffffff;
  border-top: 1px solid #ffffff;
  color: #ffffff;
  display: flex;
  font-size: 1.625rem;
  font-weight: 300;
  height: 100%;
  justify-content: center;
  padding: 1.5rem 3rem 1.5rem 1.25rem;
  position: absolute;
  top: -50%;
  left: 0;
  &:before {
    border-bottom: 1px solid #ffffff;
    border-top: 1px solid #ffffff;
    content: "";
    height: calc(100% + 2px);
    background: var(--uzpruvo-orange);
    position: absolute;
    width: 500%;
    right: 100%;
    top: -1px;
  }
  @media screen and (min-width: 1024px) {
    font-size: 2.375rem;
    padding: 0 3rem 0 0;
  }
`;

const TitlePosition = styled.div`
  grid-column: 1/7;
  position: relative;
  height: 50px;
  @media screen and (min-width: 1024px) {
    grid-column: 2/12;
    height: 80px;
  }
  @media screen and (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

const Images = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 1.125rem;
  gap: 1.5rem;
  h6 {
    font-size: 1.125rem;
    font-weight: 500;
    text-transform: uppercase;
  }
  @media screen and (min-width: 740px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    font-size: 1.375rem;
    h6 {
      font-size: 1.375rem;
      text-align: center;
    }
  }
`;

const Image = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-direction: column;
  @media screen and (min-width: 740px) {
    &:last-of-type {
      padding: 0 2rem;
    }
  }
`;

const SectionHeader = styled.div`
  background: linear-gradient(90deg, #412a67 0%, #e77c6a 100%);
  color: #ffffff;
  display: grid;
  grid-column: 1/7;
  grid-template-columns: repeat(6, 1fr);
  @media screen and (min-width: 1024px) {
    grid-column: 1/13;
    grid-template-columns: repeat(12, 1fr);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1/7;
  padding: 0 1.25rem;
  margin-bottom: 3rem;
  gap: 2rem;
  p {
    font-weight: 300;
    strong {
      font-weight: 500;
    }
  }
  @media screen and (min-width: 1024px) {
    grid-column: 2/12;
    margin-bottom: 6rem;
  }
  @media screen and (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  gap: 1.25rem;
  grid-column: 1/7;
  padding: 0 1.25rem;
  margin: 3rem 0;
  h4 {
    font-size: 1.125rem;
    color: var(--uzpruvo-purple);
    font-weight: 300;
    text-transform: uppercase;
    span {
      font-weight: 500;
    }
    p {
      color: var(--uzpruvo-purple);
      text-transform: none;
      font-weight: 500;
      font-size: 1.125rem;
      padding-top: 1.5rem;
    }
  }
  p {
    color: #1d1d1b;
    font-weight: 300;
  }
  @media screen and (min-width: 1024px) {
    font-size: 1rem;
    gap: 2rem;
    grid-column: 2/12;
    padding: 0;
    margin: 6rem 0;
    h4 {
      font-size: 1.625rem;
      p {
        font-size: 1.5rem;
      }
    }
  }
  @media screen and (min-width: 1600px) {
    grid-column: 3/11;
  }
`;

const Text = styled.p`
  font-weight: 1rem;
  @media screen and (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const SmallText = styled.p`
  font-size: 0.875rem;
  @media screen and (min-width: 1024px) {
    font-size: 1rem;
  }
`;

const ImageContainer = styled.div`
  margin: 2rem 0;
  @media screen and (min-width: 1024px) {
    width: 100%;
    max-width: 1074px;
    margin: 3rem auto;
  }
`;
export default Dosing;
